import * as React from "react";

/**
 * Headless engine for the composable wizard. Owns step state, value
 * accumulation, validation-gated navigation, and the agent seam. It knows
 * nothing about how an agent is implemented: an adapter drives proposals,
 * issues, confirmations, and narration through a controlled {@link WizardAgentChannel}.
 *
 * Pair with the `@erp/ui/wizard` compound components for the standard look,
 * or consume the returned {@link WizardApi} directly for full layout control.
 */

export type WizardValues = Record<string, unknown>;

export type WizardStepStatus =
  | "locked" // not yet reachable (an earlier required step is incomplete)
  | "available" // reachable, not yet completed
  | "active" // the current step
  | "complete" // validated / satisfied
  | "skipped"; // optional step the user passed over

export type WizardAgentActivity =
  | "idle"
  | "thinking" // streaming / working
  | "waiting"; // blocked on a human decision

export interface WizardValidationContext {
  values: WizardValues;
  api: WizardApi;
}

export type WizardValidationResult =
  | boolean
  | { ok: true }
  | { ok: false; message?: string; fields?: Record<string, string> };

export interface WizardStepDef {
  id: string;
  title: string;
  description?: string;
  /** Phosphor (or any) icon component for the rail marker. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Optional steps can be skipped without completing. */
  optional?: boolean;
  /**
   * A consequential step. The hook itself stops here and surfaces a
   * confirmation before commit, on every path (manual Next and agent alike).
   * This is the "guardrails for the consequential" lever: routine steps fly
   * by, gates never do, and no adapter can opt out of it.
   */
  gate?: boolean;
  /**
   * Confirmation shown when leaving a gate step. Static, or derived from the
   * current values. Omit to use a generic "Confirm: <title>" prompt.
   */
  confirm?: WizardGateConfirm | ((values: WizardValues) => WizardGateConfirm);
  /** Validate before leaving this step. Return `false` / `{ ok: false }` to block. */
  validate?: (
    ctx: WizardValidationContext,
  ) => WizardValidationResult | Promise<WizardValidationResult>;
  /**
   * A short, domain-agnostic brief the agent adapter can hand to its model to
   * describe what help is useful here. The wizard never interprets it.
   */
  agentBrief?: string;
}

export interface WizardProvenanceRef {
  label: string;
  href?: string;
  detail?: string;
}

export interface WizardProposalMeta {
  /** 0..1 */
  confidence?: number;
  rationale?: string;
  provenance?: WizardProvenanceRef[];
}

export interface WizardProposal {
  id: string;
  stepId: string;
  /** Field-level proposed values, keyed to match `values`. */
  values: WizardValues;
  meta: WizardProposalMeta;
  status: "pending" | "accepted" | "rejected";
}

export interface WizardIssue {
  id: string;
  stepId: string;
  message: string;
  severity: "error" | "warning";
  source: "validation" | "agent";
  field?: string;
}

export interface WizardAgentMessage {
  id: string;
  role: "agent" | "user";
  text: string;
}

export interface WizardConfirmation {
  id: string;
  title: string;
  detail?: string;
  /** Consequence lines, e.g. ["Creates 1 version", "Affects 240 line items"]. */
  consequences?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
}

/** Confirmation content for a gate step, before an id is assigned. */
export type WizardGateConfirm = Omit<WizardConfirmation, "id">;

/** Read-only view of wizard state handed to the adapter. */
export interface WizardSnapshot {
  values: WizardValues;
  activeStepId: string;
  activeStep: WizardStepDef;
  steps: Array<Pick<WizardStepDef, "id" | "title" | "gate" | "optional">>;
  statuses: Record<string, WizardStepStatus>;
  autopilotRunning: boolean;
  isComplete: boolean;
}

/**
 * The seam. An adapter receives this channel once on connect and uses it to
 * drive the wizard. Everything the agent does flows through here, so the agent
 * can only act through the same affordances a human has: it proposes, the
 * human (or an explicit autopilot grant) commits.
 */
export interface WizardAgentChannel {
  getSnapshot: () => WizardSnapshot;
  subscribe: (listener: (snapshot: WizardSnapshot) => void) => () => void;
  setActivity: (activity: WizardAgentActivity) => void;
  say: (text: string) => void;
  /** Offer field values for a step. Returns the proposal id. */
  proposeValues: (
    stepId: string,
    values: WizardValues,
    meta?: WizardProposalMeta,
  ) => string;
  flagIssue: (issue: Omit<WizardIssue, "id" | "source">) => string;
  clearIssue: (issueId: string) => void;
  /** Validate the active step and move to the next one (the agent's hand). */
  advance: () => Promise<boolean>;
  goTo: (stepId: string) => Promise<boolean>;
  /** Mark the active step as ready, surfacing a "suggested" cue on Next. */
  suggestAdvance: (stepId: string) => void;
  /** Ask the human to approve a consequential action. Resolves on their answer. */
  requestConfirmation: (
    request: Omit<WizardConfirmation, "id">,
  ) => Promise<boolean>;
}

export interface WizardAgentAdapter {
  /** Shown in the assist panel header, e.g. "Assistant". */
  name?: string;
  supportsAutopilot?: boolean;
  /** Called once when the wizard mounts. Return a cleanup function. */
  connect: (channel: WizardAgentChannel) => void | (() => void);
  onAsk?: (text: string, channel: WizardAgentChannel) => void;
  onStartAutopilot?: (channel: WizardAgentChannel) => void;
  onPauseAutopilot?: (channel: WizardAgentChannel) => void;
  /** Fired on entering a step (including the first), so help can be proactive. */
  onStepEnter?: (stepId: string, channel: WizardAgentChannel) => void;
}

export interface WizardConfig {
  steps: WizardStepDef[];
  initialValues?: WizardValues;
  initialStepId?: string;
  /** When true (default) steps must be completed in order. */
  linear?: boolean;
  /** Omit for a plain wizard with no agentic help. */
  agent?: WizardAgentAdapter;
  onComplete?: (values: WizardValues) => void | Promise<void>;
  onStepChange?: (stepId: string, previousStepId: string) => void;
}

/** The agent-facing slice of the api, read by the UI. */
export interface WizardAgentState {
  enabled: boolean;
  name: string;
  activity: WizardAgentActivity;
  messages: WizardAgentMessage[];
  proposalsOf: (stepId: string) => WizardProposal[];
  proposalForField: (
    stepId: string,
    field: string,
  ) => WizardProposal | undefined;
  acceptProposal: (proposalId: string) => void;
  rejectProposal: (proposalId: string) => void;
  acceptAll: (stepId: string) => void;
  autopilot: { available: boolean; running: boolean };
  startAutopilot: () => void;
  pauseAutopilot: () => void;
  ask: (text: string) => void;
  suggestedNextStepId: string | null;
}

export interface WizardApi {
  steps: WizardStepDef[];
  activeStepId: string;
  activeIndex: number;
  activeStep: WizardStepDef;
  isFirstStep: boolean;
  isLastStep: boolean;
  isBusy: boolean;
  isComplete: boolean;

  statusOf: (stepId: string) => WizardStepStatus;
  issuesOf: (stepId: string) => WizardIssue[];

  values: WizardValues;
  getValue: <T = unknown>(key: string) => T | undefined;
  setValue: (key: string, value: unknown) => void;
  setValues: (patch: WizardValues) => void;

  canGoNext: boolean;
  canGoBack: boolean;
  canSkip: boolean;
  next: () => Promise<boolean>;
  back: () => void;
  goTo: (stepId: string) => Promise<boolean>;
  skip: () => void;
  complete: () => Promise<boolean>;

  /** A pending gate confirmation, or null. Surfaced on the work surface. */
  confirmation: WizardConfirmation | null;
  resolveConfirmation: (approved: boolean) => void;

  agent: WizardAgentState;
}

// --- internal state -------------------------------------------------------

interface WizardState {
  values: WizardValues;
  activeStepId: string;
  furthestIndex: number;
  completed: ReadonlySet<string>;
  skipped: ReadonlySet<string>;
  proposals: WizardProposal[];
  issues: WizardIssue[];
  messages: WizardAgentMessage[];
  activity: WizardAgentActivity;
  autopilotRunning: boolean;
  confirmation: WizardConfirmation | null;
  suggestedNextStepId: string | null;
  isBusy: boolean;
  isComplete: boolean;
}

type WizardAction =
  | { type: "SET_VALUES"; patch: WizardValues }
  | { type: "GO_TO"; stepId: string; index: number }
  | { type: "MARK_COMPLETE"; stepId: string }
  | { type: "MARK_SKIPPED"; stepId: string }
  | { type: "SET_BUSY"; busy: boolean }
  | { type: "SET_WIZARD_COMPLETE"; complete: boolean }
  | { type: "ADD_PROPOSAL"; proposal: WizardProposal }
  | {
      type: "SET_PROPOSAL_STATUS";
      id: string;
      status: WizardProposal["status"];
    }
  | { type: "ADD_ISSUE"; issue: WizardIssue }
  | { type: "CLEAR_ISSUE"; id: string }
  | { type: "CLEAR_STEP_VALIDATION_ISSUES"; stepId: string }
  | { type: "ADD_MESSAGE"; message: WizardAgentMessage }
  | { type: "SET_ACTIVITY"; activity: WizardAgentActivity }
  | { type: "SET_AUTOPILOT"; running: boolean }
  | { type: "SET_CONFIRMATION"; confirmation: WizardConfirmation | null }
  | { type: "SET_SUGGESTED_NEXT"; stepId: string | null };

function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_VALUES":
      return { ...state, values: { ...state.values, ...action.patch } };
    case "GO_TO": {
      const completed = new Set(state.completed);
      return {
        ...state,
        activeStepId: action.stepId,
        furthestIndex: Math.max(state.furthestIndex, action.index),
        completed,
        suggestedNextStepId: null,
      };
    }
    case "MARK_COMPLETE": {
      const completed = new Set(state.completed);
      completed.add(action.stepId);
      const skipped = new Set(state.skipped);
      skipped.delete(action.stepId);
      return { ...state, completed, skipped };
    }
    case "MARK_SKIPPED": {
      const skipped = new Set(state.skipped);
      skipped.add(action.stepId);
      return { ...state, skipped };
    }
    case "SET_BUSY":
      return { ...state, isBusy: action.busy };
    case "SET_WIZARD_COMPLETE":
      return { ...state, isComplete: action.complete };
    case "ADD_PROPOSAL":
      return { ...state, proposals: [...state.proposals, action.proposal] };
    case "SET_PROPOSAL_STATUS":
      return {
        ...state,
        proposals: state.proposals.map((p) =>
          p.id === action.id ? { ...p, status: action.status } : p,
        ),
      };
    case "ADD_ISSUE":
      return { ...state, issues: [...state.issues, action.issue] };
    case "CLEAR_ISSUE":
      return {
        ...state,
        issues: state.issues.filter((i) => i.id !== action.id),
      };
    case "CLEAR_STEP_VALIDATION_ISSUES":
      return {
        ...state,
        issues: state.issues.filter(
          (i) => !(i.stepId === action.stepId && i.source === "validation"),
        ),
      };
    case "ADD_MESSAGE":
      return { ...state, messages: [...state.messages, action.message] };
    case "SET_ACTIVITY":
      return { ...state, activity: action.activity };
    case "SET_AUTOPILOT":
      return { ...state, autopilotRunning: action.running };
    case "SET_CONFIRMATION":
      return { ...state, confirmation: action.confirmation };
    case "SET_SUGGESTED_NEXT":
      return { ...state, suggestedNextStepId: action.stepId };
  }
}

function normalizeValidation(result: WizardValidationResult): {
  ok: boolean;
  message?: string;
  fields?: Record<string, string>;
} {
  if (result === true) return { ok: true };
  if (result === false) return { ok: false };
  if (result.ok) return { ok: true };
  return { ok: false, message: result.message, fields: result.fields };
}

export function useWizard(config: WizardConfig): WizardApi {
  const { steps, agent, linear = true } = config;

  const initialStepId = config.initialStepId ?? steps[0]?.id ?? "";
  const initialIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === initialStepId),
  );

  const [state, dispatch] = React.useReducer(reducer, undefined, () => ({
    values: { ...config.initialValues },
    activeStepId: initialStepId,
    furthestIndex: initialIndex,
    completed: new Set<string>(),
    skipped: new Set<string>(),
    proposals: [],
    issues: [],
    messages: [],
    activity: "idle" as WizardAgentActivity,
    autopilotRunning: false,
    confirmation: null,
    suggestedNextStepId: null,
    isBusy: false,
    isComplete: false,
  }));

  // Latest-state mirror so stable channel callbacks can read synchronously.
  const stateRef = React.useRef(state);
  stateRef.current = state;

  // Latest adapter/config behind refs so callbacks needn't depend on them
  // (the adapter is recreated freely by consumers; we connect it once).
  const agentRef = React.useRef(agent);
  agentRef.current = agent;
  const configRef = React.useRef(config);
  configRef.current = config;

  // Logic invoked by the (stable) channel always calls the latest closures.
  const actionsRef = React.useRef<{
    advance: () => Promise<boolean>;
    goTo: (stepId: string) => Promise<boolean>;
    snapshot: () => WizardSnapshot;
    confirm: (request: WizardGateConfirm) => Promise<boolean>;
  }>({
    advance: async () => false,
    goTo: async () => false,
    snapshot: () => ({
      values: {},
      activeStepId: "",
      activeStep: steps[0] ?? { id: "", title: "" },
      steps: [],
      statuses: {},
      autopilotRunning: false,
      isComplete: false,
    }),
    confirm: async () => false,
  });

  const idRef = React.useRef(0);
  const genId = React.useCallback((prefix: string) => {
    idRef.current += 1;
    return `${prefix}-${idRef.current}`;
  }, []);

  const confirmResolverRef = React.useRef<((approved: boolean) => void) | null>(
    null,
  );
  const listenersRef = React.useRef(
    new Set<(snapshot: WizardSnapshot) => void>(),
  );

  const indexOf = React.useCallback(
    (stepId: string) => steps.findIndex((s) => s.id === stepId),
    [steps],
  );

  const activeIndex = Math.max(0, indexOf(state.activeStepId));
  const activeStep = steps[activeIndex] ?? steps[0] ?? { id: "", title: "" };
  const lastIndex = steps.length - 1;

  const statusOf = React.useCallback(
    (stepId: string): WizardStepStatus => {
      if (state.completed.has(stepId)) return "complete";
      if (state.skipped.has(stepId)) return "skipped";
      if (stepId === state.activeStepId) return "active";
      const idx = indexOf(stepId);
      if (!linear || idx <= state.furthestIndex) return "available";
      return "locked";
    },
    [
      state.completed,
      state.skipped,
      state.activeStepId,
      state.furthestIndex,
      indexOf,
      linear,
    ],
  );

  const issuesOf = React.useCallback(
    (stepId: string) => state.issues.filter((i) => i.stepId === stepId),
    [state.issues],
  );

  const buildSnapshot = React.useCallback((): WizardSnapshot => {
    const s = stateRef.current;
    const idx = Math.max(
      0,
      steps.findIndex((st) => st.id === s.activeStepId),
    );
    const statuses: Record<string, WizardStepStatus> = {};
    for (const st of steps) statuses[st.id] = statusOf(st.id);
    return {
      values: s.values,
      activeStepId: s.activeStepId,
      activeStep: steps[idx] ?? steps[0] ?? { id: "", title: "" },
      steps: steps.map((st) => ({
        id: st.id,
        title: st.title,
        gate: st.gate,
        optional: st.optional,
      })),
      statuses,
      autopilotRunning: s.autopilotRunning,
      isComplete: s.isComplete,
    };
  }, [steps, statusOf]);

  // --- value mutators -----------------------------------------------------

  const setValue = React.useCallback((key: string, value: unknown) => {
    dispatch({ type: "SET_VALUES", patch: { [key]: value } });
  }, []);
  const setValues = React.useCallback((patch: WizardValues) => {
    dispatch({ type: "SET_VALUES", patch });
  }, []);

  // --- navigation ---------------------------------------------------------

  const goToIndex = React.useCallback(
    (targetIndex: number) => {
      const target = steps[targetIndex];
      if (!target) return;
      const previous = stateRef.current.activeStepId;
      dispatch({ type: "GO_TO", stepId: target.id, index: targetIndex });
      configRef.current.onStepChange?.(target.id, previous);
      const ch = channelRef.current;
      if (ch) agentRef.current?.onStepEnter?.(target.id, ch);
    },
    [steps],
  );

  const validateActive = React.useCallback(async (): Promise<boolean> => {
    const step = steps[Math.max(0, indexOf(stateRef.current.activeStepId))];
    if (!step?.validate) return true;
    dispatch({ type: "CLEAR_STEP_VALIDATION_ISSUES", stepId: step.id });
    const result = await step.validate({
      values: stateRef.current.values,
      api: apiRef.current,
    });
    const normalized = normalizeValidation(result);
    if (normalized.ok) return true;
    if (normalized.fields) {
      for (const [field, message] of Object.entries(normalized.fields)) {
        dispatch({
          type: "ADD_ISSUE",
          issue: {
            id: genId("issue"),
            stepId: step.id,
            message,
            severity: "error",
            source: "validation",
            field,
          },
        });
      }
    }
    if (normalized.message || !normalized.fields) {
      dispatch({
        type: "ADD_ISSUE",
        issue: {
          id: genId("issue"),
          stepId: step.id,
          message:
            normalized.message ?? "Please complete this step to continue.",
          severity: "error",
          source: "validation",
        },
      });
    }
    return false;
  }, [steps, indexOf, genId]);

  // Surface a confirmation and resolve when the human answers. Shared by gate
  // enforcement (below) and the agent channel, so there is exactly one path.
  // There is a single confirmation slot: if one is already pending, refuse the
  // new request (resolve false) rather than overwriting the resolver and
  // stranding the first caller's promise. The resolver ref is set synchronously
  // here, so even a re-entrant call before the next render is caught.
  const requestConfirmationInternal = React.useCallback(
    (request: WizardGateConfirm): Promise<boolean> => {
      if (confirmResolverRef.current) return Promise.resolve(false);
      return new Promise<boolean>((resolve) => {
        confirmResolverRef.current = resolve;
        dispatch({
          type: "SET_CONFIRMATION",
          confirmation: { ...request, id: genId("confirm") },
        });
        dispatch({ type: "SET_ACTIVITY", activity: "waiting" });
      });
    },
    [genId],
  );

  const requestGateConfirmation = React.useCallback(
    (step: WizardStepDef): Promise<boolean> => {
      const raw =
        typeof step.confirm === "function"
          ? step.confirm(stateRef.current.values)
          : step.confirm;
      return requestConfirmationInternal(
        raw ?? { title: `Confirm: ${step.title}` },
      );
    },
    [requestConfirmationInternal],
  );

  const complete = React.useCallback(async (): Promise<boolean> => {
    dispatch({ type: "SET_BUSY", busy: true });
    try {
      const ok = await validateActive();
      if (!ok) return false;
      const step = steps[Math.max(0, indexOf(stateRef.current.activeStepId))];
      if (step?.gate) {
        dispatch({ type: "SET_BUSY", busy: false });
        const approved = await requestGateConfirmation(step);
        if (!approved) return false;
        dispatch({ type: "SET_BUSY", busy: true });
      }
      dispatch({
        type: "MARK_COMPLETE",
        stepId: stateRef.current.activeStepId,
      });
      dispatch({ type: "SET_WIZARD_COMPLETE", complete: true });
      dispatch({ type: "SET_AUTOPILOT", running: false });
      dispatch({ type: "SET_ACTIVITY", activity: "idle" });
      await config.onComplete?.(stateRef.current.values);
      return true;
    } finally {
      dispatch({ type: "SET_BUSY", busy: false });
    }
  }, [validateActive, config, steps, indexOf, requestGateConfirmation]);

  const advance = React.useCallback(async (): Promise<boolean> => {
    const idx = Math.max(0, indexOf(stateRef.current.activeStepId));
    if (idx >= lastIndex) return complete();
    dispatch({ type: "SET_BUSY", busy: true });
    try {
      const ok = await validateActive();
      if (!ok) return false;
      const step = steps[idx];
      if (step?.gate) {
        dispatch({ type: "SET_BUSY", busy: false });
        const approved = await requestGateConfirmation(step);
        if (!approved) return false;
        dispatch({ type: "SET_BUSY", busy: true });
      }
      dispatch({
        type: "MARK_COMPLETE",
        stepId: stateRef.current.activeStepId,
      });
      goToIndex(idx + 1);
      return true;
    } finally {
      dispatch({ type: "SET_BUSY", busy: false });
    }
  }, [
    indexOf,
    lastIndex,
    complete,
    validateActive,
    goToIndex,
    steps,
    requestGateConfirmation,
  ]);

  const back = React.useCallback(() => {
    const idx = Math.max(0, indexOf(stateRef.current.activeStepId));
    if (idx > 0) goToIndex(idx - 1);
  }, [indexOf, goToIndex]);

  const goTo = React.useCallback(
    async (stepId: string): Promise<boolean> => {
      const idx = indexOf(stepId);
      if (idx < 0) return false;
      // Backward / reachable navigation is free; jumping past the frontier is not.
      if (linear && idx > stateRef.current.furthestIndex) return false;
      goToIndex(idx);
      return true;
    },
    [indexOf, linear, goToIndex],
  );

  const skip = React.useCallback(() => {
    const idx = Math.max(0, indexOf(stateRef.current.activeStepId));
    const step = steps[idx];
    if (!step?.optional) return;
    dispatch({ type: "MARK_SKIPPED", stepId: step.id });
    if (idx < lastIndex) goToIndex(idx + 1);
    else dispatch({ type: "SET_WIZARD_COMPLETE", complete: true });
  }, [indexOf, steps, lastIndex, goToIndex]);

  // --- agent surface ------------------------------------------------------

  const acceptProposal = React.useCallback((proposalId: string) => {
    const proposal = stateRef.current.proposals.find(
      (p) => p.id === proposalId,
    );
    if (!proposal) return;
    dispatch({ type: "SET_VALUES", patch: proposal.values });
    dispatch({
      type: "SET_PROPOSAL_STATUS",
      id: proposalId,
      status: "accepted",
    });
  }, []);
  const rejectProposal = React.useCallback((proposalId: string) => {
    dispatch({
      type: "SET_PROPOSAL_STATUS",
      id: proposalId,
      status: "rejected",
    });
  }, []);
  const acceptAll = React.useCallback((stepId: string) => {
    for (const p of stateRef.current.proposals) {
      if (p.stepId === stepId && p.status === "pending") {
        dispatch({ type: "SET_VALUES", patch: p.values });
        dispatch({ type: "SET_PROPOSAL_STATUS", id: p.id, status: "accepted" });
      }
    }
  }, []);

  const resolveConfirmation = React.useCallback((approved: boolean) => {
    const resolver = confirmResolverRef.current;
    confirmResolverRef.current = null;
    dispatch({ type: "SET_CONFIRMATION", confirmation: null });
    dispatch({
      type: "SET_ACTIVITY",
      activity:
        approved && stateRef.current.autopilotRunning ? "thinking" : "idle",
    });
    // Declining a gate also stops autopilot: the human took back the wheel.
    if (!approved && stateRef.current.autopilotRunning) {
      dispatch({ type: "SET_AUTOPILOT", running: false });
    }
    resolver?.(approved);
  }, []);

  const startAutopilot = React.useCallback(() => {
    if (!agent?.supportsAutopilot) return;
    dispatch({ type: "SET_AUTOPILOT", running: true });
    dispatch({ type: "SET_ACTIVITY", activity: "thinking" });
    const ch = channelRef.current;
    if (ch) agent.onStartAutopilot?.(ch);
  }, [agent]);

  const pauseAutopilot = React.useCallback(() => {
    dispatch({ type: "SET_AUTOPILOT", running: false });
    dispatch({ type: "SET_ACTIVITY", activity: "idle" });
    const ch = channelRef.current;
    if (ch && agent) agent.onPauseAutopilot?.(ch);
  }, [agent]);

  const ask = React.useCallback(
    (text: string) => {
      if (!text.trim()) return;
      dispatch({
        type: "ADD_MESSAGE",
        message: { id: genId("msg"), role: "user", text },
      });
      const ch = channelRef.current;
      if (ch && agent) agent.onAsk?.(text, ch);
    },
    [agent, genId],
  );

  // --- stable channel for the adapter ------------------------------------

  const channelRef = React.useRef<WizardAgentChannel | null>(null);
  const channel = React.useMemo<WizardAgentChannel>(
    () => ({
      getSnapshot: () => actionsRef.current.snapshot(),
      subscribe: (listener) => {
        listenersRef.current.add(listener);
        return () => listenersRef.current.delete(listener);
      },
      setActivity: (activity) => dispatch({ type: "SET_ACTIVITY", activity }),
      say: (text) =>
        dispatch({
          type: "ADD_MESSAGE",
          message: { id: genId("msg"), role: "agent", text },
        }),
      proposeValues: (stepId, values, meta) => {
        const id = genId("prop");
        // Under autopilot the human has granted a standing commit for routine
        // work, so the proposal is applied immediately and still recorded for
        // provenance. Manual mode keeps it pending until accepted at the point
        // of decision. A gate step always stops regardless (see the adapter).
        const running = stateRef.current.autopilotRunning;
        if (running) dispatch({ type: "SET_VALUES", patch: values });
        dispatch({
          type: "ADD_PROPOSAL",
          proposal: {
            id,
            stepId,
            values,
            meta: meta ?? {},
            status: running ? "accepted" : "pending",
          },
        });
        return id;
      },
      flagIssue: (issue) => {
        const id = genId("issue");
        dispatch({
          type: "ADD_ISSUE",
          issue: { ...issue, id, source: "agent" },
        });
        return id;
      },
      clearIssue: (issueId) => dispatch({ type: "CLEAR_ISSUE", id: issueId }),
      advance: () => actionsRef.current.advance(),
      goTo: (stepId) => actionsRef.current.goTo(stepId),
      suggestAdvance: (stepId) =>
        dispatch({ type: "SET_SUGGESTED_NEXT", stepId }),
      requestConfirmation: (request) => actionsRef.current.confirm(request),
    }),
    [genId],
  );

  // Keep the channel's read-side closures pointed at the latest logic.
  actionsRef.current.advance = advance;
  actionsRef.current.goTo = goTo;
  actionsRef.current.snapshot = buildSnapshot;
  actionsRef.current.confirm = requestConfirmationInternal;
  channelRef.current = channel;

  // Connect the adapter once at mount, then announce the first step. Reads
  // through refs so it needn't re-run when the adapter or channel re-identify.
  React.useEffect(() => {
    const adapter = agentRef.current;
    const ch = channelRef.current;
    if (!adapter || !ch) return;
    const cleanup = adapter.connect(ch);
    adapter.onStepEnter?.(stateRef.current.activeStepId, ch);
    return typeof cleanup === "function" ? cleanup : undefined;
  }, []);

  // Notify adapter subscribers when observable state changes.
  React.useEffect(() => {
    if (listenersRef.current.size === 0) return;
    const snapshot = buildSnapshot();
    for (const listener of listenersRef.current) listener(snapshot);
  }, [state.values, state.activeStepId, state.autopilotRunning, buildSnapshot]);

  const agentState: WizardAgentState = {
    enabled: Boolean(agent),
    name: agent?.name ?? "Assistant",
    activity: state.activity,
    messages: state.messages,
    proposalsOf: (stepId) =>
      state.proposals.filter(
        (p) => p.stepId === stepId && p.status === "pending",
      ),
    proposalForField: (stepId, field) =>
      state.proposals.find(
        (p) =>
          p.stepId === stepId &&
          p.status === "pending" &&
          Object.prototype.hasOwnProperty.call(p.values, field),
      ),
    acceptProposal,
    rejectProposal,
    acceptAll,
    autopilot: {
      available: Boolean(agent?.supportsAutopilot),
      running: state.autopilotRunning,
    },
    startAutopilot,
    pauseAutopilot,
    ask,
    suggestedNextStepId: state.suggestedNextStepId,
  };

  const api: WizardApi = {
    steps,
    activeStepId: state.activeStepId,
    activeIndex,
    activeStep,
    isFirstStep: activeIndex === 0,
    isLastStep: activeIndex === lastIndex,
    isBusy: state.isBusy,
    isComplete: state.isComplete,
    statusOf,
    issuesOf,
    values: state.values,
    getValue: <T>(key: string) => state.values[key] as T | undefined,
    setValue,
    setValues,
    canGoNext: activeIndex < lastIndex,
    canGoBack: activeIndex > 0,
    canSkip: Boolean(activeStep.optional) && activeIndex < lastIndex,
    next: advance,
    back,
    goTo,
    skip,
    complete,
    confirmation: state.confirmation,
    resolveConfirmation,
    agent: agentState,
  };

  const apiRef = React.useRef(api);
  apiRef.current = api;

  return api;
}
